import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import MermaidDiagram from './MermaidDiagram';

const MermaidDemo = () => {
  const [activeTab, setActiveTab] = useState('flowchart');

  const examples = {
    flowchart: {
      title: 'Flowchart',
      description: 'Visual representation of a process or workflow with colorful nodes',
      code: `flowchart TD
    A[Start]:::startClass --> B{Is it Working?}:::decisionClass
    B -->|Yes| C[Great!]:::successClass
    B -->|No| D[Debug]:::processClass
    D --> E[Fix Issues]:::processClass
    E --> B
    C --> F[End]:::endClass
    
    classDef startClass fill:#6366f1,stroke:#4f46e5,color:#fff
    classDef decisionClass fill:#ec4899,stroke:#db2777,color:#fff
    classDef successClass fill:#10b981,stroke:#059669,color:#fff
    classDef processClass fill:#f59e0b,stroke:#d97706,color:#fff
    classDef endClass fill:#8b5cf6,stroke:#7c3aed,color:#fff`
    },
    sequence: {
      title: 'Sequence Diagram',
      description: 'Show interactions between objects in sequential order',
      code: `sequenceDiagram
    participant User
    participant Frontend
    participant Backend
    participant Database
    
    User->>Frontend: Login Request
    Frontend->>Backend: POST /api/auth/login
    Backend->>Database: Verify Credentials
    Database-->>Backend: User Data
    Backend-->>Frontend: JWT Token
    Frontend-->>User: Login Success`
    },
    gantt: {
      title: 'Gantt Chart',
      description: 'Colorful project timeline and task scheduling',
      code: `gantt
    title Nerd AI Development Timeline
    dateFormat YYYY-MM-DD
    section Backend
    API Development           :done, a1, 2024-01-01, 30d
    Database Setup           :active, a2, after a1, 20d
    section Frontend
    UI Design                :done, b1, 2024-01-01, 25d
    Component Development    :active, b2, after b1, 35d
    section Testing
    Testing & QA             :crit, c1, after b2, 15d
    Deployment              :c2, after c1, 5d`
    },
    classDiagram: {
      title: 'Class Diagram',
      description: 'Object-oriented design structure',
      code: `classDiagram
    class User {
        +String id
        +String email
        +String name
        +login()
        +logout()
    }
    class Exam {
        +String id
        +String title
        +Array questions
        +startExam()
        +submitExam()
    }
    class Question {
        +String id
        +String text
        +Array options
        +checkAnswer()
    }
    User "1" --> "*" Exam : takes
    Exam "1" --> "*" Question : contains`
    },
    erDiagram: {
      title: 'ER Diagram',
      description: 'Database entity relationships',
      code: `erDiagram
    USERS ||--o{ EXAM_ATTEMPTS : takes
    USERS {
        string id PK
        string email
        string name
        date created_at
    }
    EXAMS ||--o{ EXAM_ATTEMPTS : has
    EXAMS {
        string id PK
        string title
        int duration
        date created_at
    }
    EXAM_ATTEMPTS {
        string id PK
        string user_id FK
        string exam_id FK
        int score
        date completed_at
    }`
    },
    pie: {
      title: 'Pie Chart',
      description: 'Data distribution visualization',
      code: `pie title Student Distribution by Exam Type
    "Medical Exams" : 35
    "Engineering Exams" : 25
    "Global Exams (GRE/GMAT)" : 20
    "Pharmacy Exams" : 12
    "Dental Exams" : 8`
    },
    mindmap: {
      title: 'Mind Map',
      description: 'Hierarchical information visualization',
      code: `mindmap
  root((Nerd AI Platform))
    Exam Preparation
      Medical
        PLAB
        USMLE
        AMC
      Engineering
        IIT-JEE
        NEET-UG
      Global
        GRE
        GMAT
    AI Features
      Career Advisor
      Research Helper
      Ask Nerd AI
    Community
      Friends
      Chat
      Study Groups`
    },
    timeline: {
      title: 'Timeline',
      description: 'Chronological event sequence',
      code: `timeline
    title Nerd AI Platform Journey
    2024-Q1 : Platform Launch
              : User Registration Opens
    2024-Q2 : AI Career Advisor
              : 1000+ Users
    2024-Q3 : Exam Bank Expansion
              : Global Exams Added
    2024-Q4 : Social Features
              : Chat System
              : 5000+ Users`
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Mermaid.js Diagrams
          </h1>
          <p className="text-lg text-gray-600">
            Create beautiful diagrams and visualizations with simple text syntax
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-8 justify-center">
          {Object.entries(examples).map(([key, { title }]) => (
            <Button
              key={key}
              onClick={() => setActiveTab(key)}
              variant={activeTab === key ? 'default' : 'outline'}
              className={activeTab === key ? 'bg-indigo-600 hover:bg-indigo-700' : ''}
            >
              {title}
            </Button>
          ))}
        </div>

        {/* Active Diagram */}
        <Card className="shadow-xl">
          <CardContent className="p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {examples[activeTab].title}
              </h2>
              <p className="text-gray-600">
                {examples[activeTab].description}
              </p>
            </div>

            {/* Diagram Display */}
            <div className="bg-white border-2 border-gray-200 rounded-lg p-6 mb-6 overflow-x-auto">
              <MermaidDiagram 
                chart={examples[activeTab].code}
                className="flex justify-center"
              />
            </div>

            {/* Code Display */}
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm text-gray-100">
                <code>{examples[activeTab].code}</code>
              </pre>
            </div>
          </CardContent>
        </Card>

        {/* Usage Instructions */}
        <Card className="mt-8 shadow-lg">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              How to Use in Your Components
            </h3>
            <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm text-gray-100">
                <code>{`import MermaidDiagram from './components/MermaidDiagram';

// In your component
<MermaidDiagram 
  chart={\`flowchart TD
    A[Start] --> B[Process]
    B --> C[End]\`}
  className="my-custom-class"
/>`}</code>
              </pre>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MermaidDemo;
